import { useEffect, useMemo, useState } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FaExclamationTriangle } from "react-icons/fa";
import { Controller, useForm } from "react-hook-form";
import { ModalCustom } from "components/ModalCustom";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInputModal } from "components/CustomInputModal";
import { InputBusca } from "components/InputBusca";
import { toastAtencao } from "components/Toast";
import { useEmpresas } from "hooks/useEmpresas";
import { useProcessosElegiveis } from "../hooks/useProcessosElegiveis";
import { useAbrirProcesso } from "../hooks/useAbrirProcesso";
import {
  AcoesContainer,
  BuscaContainer,
  Button,
  ContadorSelecionados,
  FiltroContainer,
  TabelaContainer,
} from "./style";

export interface IProcessoTaxa {
  idProcesso: number;
  placa: string;
  renavam: string;
  chassi?: string;
  empresa: string;
  clienteFinal: string;
  cpfCnpj: string | null;
  uf: string;
  municipioOrigem?: string | null;
  tipoPgto: string | null;
  idEmpresa?: number | null;
  // Preenchidos só quando carregado de um lote existente (tela de detalhe).
  statusPagamento?: number | null;
  valor?: number | null;
  dataHoraPagamento?: string | null;
  observacao?: string | null;
  detalheDebitos?: { tipo: string; valor: number }[];
  idImagem?: number | null;
}

interface IFiltroProcessos {
  idEmpresa: number | null;
  idsProcessos: string[];
  dadosVeiculos: string[];
}

const TOOLTIP_SEM_ORIGEM =
  "Não identificado UF e Município de origem do veículo";

export const semOrigemVeiculo = (p: IProcessoTaxa) =>
  !p.uf?.trim() && !p.municipioOrigem?.trim();

const VALORES_INICIAIS: IFiltroProcessos = {
  idEmpresa: null,
  idsProcessos: [],
  dadosVeiculos: [],
};

interface IProps {
  show: boolean;
  onClose: () => void;
  onAdicionar: (processos: IProcessoTaxa[]) => void;
  idsNoLote?: number[];
}

export function ModalSelecionarProcessos({
  show,
  onClose,
  onAdicionar,
  idsNoLote,
}: IProps) {
  const { empresasOptions, loading: carregandoEmpresas } = useEmpresas();
  const { control, reset, handleSubmit } = useForm<IFiltroProcessos>({
    defaultValues: VALORES_INICIAIS,
  });

  const { loading, dados, totalRows, buscar, limpar } = useProcessosElegiveis();
  const { redirecionarProcesso } = useAbrirProcesso();

  const [busca, setBusca] = useState("");

  const [selecionados, setSelecionados] = useState<Map<number, IProcessoTaxa>>(
    new Map(),
  );
  const [limparSelecao, setLimparSelecao] = useState(false);
  const [limite, setLimite] = useState(10);
  const [filtroAtual, setFiltroAtual] = useState<IFiltroProcessos>(VALORES_INICIAIS);
  const [buscou, setBuscou] = useState(false);

  const resetarTudo = () => {
    reset(VALORES_INICIAIS);
    setFiltroAtual(VALORES_INICIAIS);
    setBuscou(false);
    setBusca("");
    setSelecionados(new Map());
    setLimparSelecao(true);
    setTimeout(() => setLimparSelecao(false), 0);
    limpar();
  };

  const handleFechar = () => {
    resetarTudo();
    onClose();
  };

  useEffect(() => {
    if (show) resetarTudo();
  }, [show]);

  const dadosFiltrados = useMemo(() => {
    if (!busca.trim()) return dados;
    const termo = busca.trim().toLowerCase();
    return dados.filter((p) =>
      [p.idProcesso, p.placa, p.renavam, p.empresa, p.clienteFinal, p.cpfCnpj]
        .join(" ")
        .toLowerCase()
        .includes(termo),
    );
  }, [busca, dados]);

  const dadosTabela = useMemo(
    () =>
      dadosFiltrados.map((p) => ({ ...p, selectable: !semOrigemVeiculo(p) })),
    [dadosFiltrados],
  );

  const handleBuscar = (filtros: IFiltroProcessos) => {
    const temFiltro =
      !!filtros.idEmpresa ||
      filtros.idsProcessos.length > 0 ||
      filtros.dadosVeiculos.length > 0;
    if (!temFiltro) {
      toastAtencao("Informe ao menos um filtro para buscar os processos.");
      return;
    }
    setFiltroAtual(filtros);
    setBuscou(true);
    buscar({ ...filtros, idsExcluir: idsNoLote }, 1, limite);
  };

  const handleLimpar = () => {
    resetarTudo();
  };

  const handlePageChange = (novaPagina: number) => {
    if (!buscou) return;
    buscar({ ...filtroAtual, idsExcluir: idsNoLote }, novaPagina, limite);
  };

  const handleLimitChange = (novoLimite: number) => {
    setLimite(novoLimite);
    if (!buscou) return;
    buscar({ ...filtroAtual, idsExcluir: idsNoLote }, 1, novoLimite);
  };

  const handleAbrirProcesso = (idProcesso: number) => {
    // TODO(DAD-2728): abrir o detalhe do processo.
    toastAtencao(`Abrir processo ${idProcesso} em construção.`);
  };

  const reconciliarSelecao = (linhasPagina: IProcessoTaxa[]) => {
    if (loading) return;
    setSelecionados((anteriores) => {
      const proximos = new Map(anteriores);
      dadosFiltrados.forEach((p) => proximos.delete(p.idProcesso));
      linhasPagina.forEach((p) => proximos.set(p.idProcesso, p));
      const igual =
        proximos.size === anteriores.size &&
        Array.from(proximos.keys()).every((id) => anteriores.has(id));
      return igual ? anteriores : proximos;
    });
  };

  const handleAdicionar = () => {
    onAdicionar(Array.from(selecionados.values()));
    handleFechar();
  };

  const columns: IColum<IProcessoTaxa>[] = [
    {
      name: "Processo",
      selector: ({ idProcesso }) => idProcesso,
      sortable: true,
      cell: ({ idProcesso }) => (
        <a
          href="#processo"
          onClick={(e) => {
            e.preventDefault();
            redirecionarProcesso(idProcesso);
          }}
        >
          {idProcesso}
        </a>
      ),
      width: "110px",
    },
    {
      name: "Placa",
      selector: ({ placa }) => placa ?? "-",
      sortable: true,
      width: "110px",
    },
    {
      name: "Renavam",
      selector: ({ renavam }) => renavam ?? "-",
      sortable: true,
      width: "140px",
    },
    {
      name: "Empresa",
      selector: ({ empresa }) => empresa ?? "-",
      sortable: true,
    },
    {
      name: "Cliente final",
      selector: ({ clienteFinal }) => clienteFinal ?? "-",
      sortable: true,
    },
    {
      name: "CPF/CNPJ",
      selector: ({ cpfCnpj }) => cpfCnpj ?? "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "UF Origem",
      selector: ({ uf }) => uf ?? "-",
      sortable: true,
      width: "110px",
    },
    {
      name: "Tipo de pgto",
      selector: ({ tipoPgto }) => tipoPgto ?? "-",
      sortable: true,
      width: "150px",
      cell: (row) =>
        semOrigemVeiculo(row) ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
              
                {TOOLTIP_SEM_ORIGEM}
              </Tooltip>
            }
          >
            <span style={{ display: "inline-flex", cursor: "pointer" }}>
              <FaExclamationTriangle color="#FF9F42" size={15} />
            </span>
          </OverlayTrigger>
        ) : (
          row.tipoPgto ?? "-"
        ),
    },
  ];

  return (
      <ModalCustom
        show={show}
        handleClose={handleFechar}
        title="Selecionar processos"
        subTitle="Selecione o(s) processo(s) que deseja realizar o pagamento da taxa."
        size="xl"
        dialogClassName="modal-selecionar-processos"
        centered
        footer
        footerConfirmButton
        cancelButtonText="Cancelar"
        confirmButtonText="Adicionar processos"
        onCancel={handleFechar}
        onConfirm={handleAdicionar}
        disabledConfirmButton={!selecionados.size}
      >
      <FiltroContainer>
        <Form noValidate onSubmit={handleSubmit(handleBuscar)}>
          <Row>
            <Col className="filtro-col" xs={12} md={6} lg>
              <Form.Group>
                <Form.Label>Empresa</Form.Label>
                <Controller
                  control={control}
                  name="idEmpresa"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      title=""
                      options={empresasOptions}
                      selectedValue={value ?? undefined}
                      onSelect={onChange}
                      searchable
                      loading={carregandoEmpresas}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col className="filtro-col" xs={12} md={6} lg>
              <Form.Group>
                <Form.Label>ID Processo</Form.Label>
                <Controller
                  control={control}
                  name="idsProcessos"
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <CustomInputModal
                      label="Filtrar em massa"
                      value={value ?? []}
                      placeholder="Busque por ID"
                      onConfirm={onChange}
                      validar={false}
                      style={{ width: "100%" }}
                      placeholderModal="Digite ou cole os IDs dos processos e tecle ENTER"
                      labelItem="Processos"
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col className="filtro-col" xs={12} md={6} lg>
              <Form.Group>
                <Form.Label>Veículo</Form.Label>
                <Controller
                  control={control}
                  name="dadosVeiculos"
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <CustomInputModal
                      label="Filtrar em massa"
                      value={value ?? []}
                      placeholder="Placa, chassi ou renavam"
                      onConfirm={onChange}
                      validar={false}
                      style={{ width: "100%" }}
                      placeholderModal="Digite ou cole placa, chassi ou renavam e tecle ENTER"
                      labelItem="Veículos"
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col className="filtro-col" xs={12} lg="auto">
              <AcoesContainer>
                <Button
                  type="reset"
                  outline
                  variante="primary"
                  onClick={handleLimpar}
                >
                  Limpar
                </Button>
                <Button type="submit" variante="black">
                  Buscar
                </Button>
              </AcoesContainer>
            </Col>
          </Row>
        </Form>
      </FiltroContainer>

      <BuscaContainer>
        <InputBusca value={busca} onChange={setBusca} />
      </BuscaContainer>

      <ContadorSelecionados>
        {selecionados.size}{" "}
        {selecionados.size === 1
          ? "processo selecionado"
          : "processos selecionados"}
      </ContadorSelecionados>

      <TabelaContainer>
        <Table
          keyField="idProcesso"
          columns={columns}
          data={dadosTabela}
          totalRows={totalRows}
          pagination={true}
          paginationRowsPerPageOptions={[10, 30, 50]}
          rowsPerPage={limite}
          loading={loading}
          noSubHeader
          possuiTooltip={false}
          selectableRows
          setRowsSelected={reconciliarSelecao}
          selectableRowSelected={(row) => selecionados.has(row.idProcesso)}
          clearSelectedRows={limparSelecao}
          headerFixo
          fixedHeaderScrollHeight="calc(100vh - 460px)"
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleLimitChange}
        />
      </TabelaContainer>
      </ModalCustom>
  );
}
